import { ContextStore } from '../stores';
import schemas from '../validation/schemas';
import logger from '../debug';
import { ListenerOptions, ListenerTrackerContext, NavigationEvent, navigationEvents } from './types';
import { AnchorData, getEventData, MdatData } from './get-event-data';
import { getFileProperties } from './url-helper';

const isEvent = (event: unknown, name: string): event is (data: Record<string, unknown>) => Promise<unknown> =>
  Object.keys(schemas).includes(name) && typeof event === 'function';

const downloadExtensionTypes = [
  'pdf',
  'xlsx',
  'docx',
  'pptx',
  'ppt',
  'odt',
  'zip',
  'rar',
  'rtf',
  'txt',
  'odp',
  'mp4',
  'jpg',
] as const;

// Deze be
const trackNamedEvent = async ({ name, attributeData }: MdatData, { tracker }: ListenerTrackerContext) => {
  if (!name) return;
  logger.debug('listener data', { name, attributeData });

  const event: unknown = tracker[name as keyof ListenerTrackerContext['tracker']];
  if (isEvent(event, name)) {
    await event(attributeData);
  }
};

const trackNavigationEvent = async (
  { name, attributeData, anchorData }: MdatData,
  event: MouseEvent,
  { tracker, lastPageEventTimeStamp = 0 }: ListenerTrackerContext,
) => {
  const referrerOutdated = event.timeStamp <= lastPageEventTimeStamp;
  const data = { ...anchorData?.eventData, ...attributeData } as any;
  logger.debug('listener data', { name, data });

  const linkType = (name || anchorData?.type) as NavigationEvent;

  if (linkType !== 'referrer') {
    if (anchorData) {
      event.preventDefault();
      setTimeout(() => {
        const event = new MouseEvent('click', { bubbles: false });
        anchorData.anchor.dispatchEvent(event);
      }, 100);
    }

    await tracker[linkType](data);
  }

  if (referrerOutdated) {
    logger.warn('not adding referrer, because page already tracked');
  } else {
    tracker.referrer(data);
  }
};

const getIsInternalLinkFn = (isInternalLink: ListenerOptions['isInternalLink']) => {
  if (typeof isInternalLink === 'function') return isInternalLink;

  const regexp = isInternalLink instanceof RegExp ? isInternalLink : new RegExp(`^${isInternalLink || ''}`);
  return (url: URL) => window.location.host === url.host && regexp.test(url.pathname);
};

const getIsDownloadLinkFn = (isDownloadLink: ListenerOptions['isDownloadLink']) => {
  if (typeof isDownloadLink === 'function') return isDownloadLink;

  return (url: URL) => {
    const fileProperties = getFileProperties(url);

    if (fileProperties.extension) {
      return (downloadExtensionTypes as readonly string[]).includes(fileProperties.extension);
    }

    return false;
  };
};

const getNavigationEventTypeFn = (options: ListenerOptions): ((url: URL) => NavigationEvent) => {
  const isDownloadLink = getIsDownloadLinkFn(options.isDownloadLink);
  const isInternalLink = getIsInternalLinkFn(options.isInternalLink);

  return (url: URL) => {
    if (isDownloadLink(url)) {
      return 'fileDownloaded';
    }

    if (isInternalLink(url)) {
      return 'referrer';
    }

    return 'exitLinkClicked';
  };
};

const initShouldBeTracked = ({ autoLinkTrackingDisabled }: ListenerOptions): ((anchorData: AnchorData) => boolean) => {
  if (typeof autoLinkTrackingDisabled === 'object') {
    return ({ type }) => {
      return !autoLinkTrackingDisabled[type];
    };
  }
  return () => !autoLinkTrackingDisabled;
};

export const createListener = (trackerContext: ListenerTrackerContext, contextStore: ContextStore, options: ListenerOptions) => {
  const getNavigationEventType = getNavigationEventTypeFn(options);
  const shouldBeTracked = initShouldBeTracked(options);

  return async (event: MouseEvent) => {
    logger.debug('click detected');
    const eventData = getEventData(event.target as HTMLElement, getNavigationEventType);
    const { name, anchorData } = eventData;

    const isExplicitEvent = !!name;
    const isNavigationEvent = navigationEvents.includes(name as NavigationEvent) || (!name && anchorData);

    if (isExplicitEvent && !isNavigationEvent) {
      contextStore.resetReferrer();
      await trackNamedEvent(eventData, trackerContext);
    } else if (isNavigationEvent && (isExplicitEvent || shouldBeTracked(anchorData!))) {
      await trackNavigationEvent(eventData, event, trackerContext);
    }
  };
};
